import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { persistCartSession } from "@/hooks/useTracking";
import { useAuth } from "@/contexts/AuthContext";

interface CartItem {
  id: string;
  name: string;
  price_cents: number;
  primary_photo?: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  isInCart: (id: string) => boolean;
  total: number;
  minutesUntilExpiry: number | null;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "premade-cart";
const CART_ACTIVITY_KEY = "premade-cart-activity";
const CART_EXPIRY_MS = 2 * 60 * 60 * 1000; // 2 hours

function getLastActivity(): number | null {
  const val = localStorage.getItem(CART_ACTIVITY_KEY);
  return val ? parseInt(val, 10) : null;
}

function setLastActivity() {
  localStorage.setItem(CART_ACTIVITY_KEY, Date.now().toString());
}

function calcMinutesRemaining(): number | null {
  const last = getLastActivity();
  if (!last) return null;
  const remaining = CART_EXPIRY_MS - (Date.now() - last);
  if (remaining <= 0) return 0;
  return Math.ceil(remaining / 60000);
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // When true, the items→[] change was triggered by automatic expiry, NOT a
  // deliberate user action. We skip the DB sync so the cart_sessions record
  // retains the last known item snapshot for abandoned-cart analytics.
  const skipDbSyncRef = useRef(false);

  const [items, setItems] = useState<CartItem[]>(() => {
    const lastActivity = getLastActivity();
    if (lastActivity && Date.now() - lastActivity > CART_EXPIRY_MS) {
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem(CART_ACTIVITY_KEY);
      // Cart expired before this page load — DB record already holds the
      // abandoned snapshot; no write needed here.
      return [];
    }
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [minutesUntilExpiry, setMinutesUntilExpiry] = useState<number | null>(
    () => (items.length > 0 ? calcMinutesRemaining() : null)
  );

  // Persist cart to localStorage and update activity timestamp when items change.
  // Skip DB sync when the change was triggered by automatic expiry so we don't
  // overwrite the abandoned-cart snapshot used in analytics.
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    if (items.length > 0) {
      setLastActivity();
      setMinutesUntilExpiry(calcMinutesRemaining());
    } else {
      localStorage.removeItem(CART_ACTIVITY_KEY);
      setMinutesUntilExpiry(null);
    }

    if (skipDbSyncRef.current) {
      // Reset flag — preserve the DB abandoned snapshot, do NOT overwrite with []
      skipDbSyncRef.current = false;
      return;
    }

    persistCartSession(
      "premade",
      items.map((i) => ({ id: i.id, name: i.name, price_cents: i.price_cents, quantity: 1 })),
      user?.id ?? null,
      false
    );
  }, [items, user?.id]);

  // Tick every minute to update countdown and auto-expire.
  // Sets skipDbSyncRef BEFORE clearing items so the effect above knows NOT to
  // overwrite the DB record with an empty array — preserving the abandoned snapshot.
  useEffect(() => {
    if (items.length === 0) return;
    const interval = setInterval(() => {
      const last = getLastActivity();
      if (last && Date.now() - last > CART_EXPIRY_MS) {
        localStorage.removeItem(CART_STORAGE_KEY);
        localStorage.removeItem(CART_ACTIVITY_KEY);
        skipDbSyncRef.current = true; // ← preserve DB snapshot for analytics
        setItems([]);
        setMinutesUntilExpiry(null);
      } else {
        setMinutesUntilExpiry(calcMinutesRemaining());
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [items.length]);

  const addItem = (item: CartItem) => {
    if (!items.find((i) => i.id === item.id)) {
      setItems((prev) => [...prev, item]);
    }
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const clearCart = () => {
    // Intentional user clear (post-checkout) — DOES sync to DB (converted state
    // is set by the checkout flow separately; this just empties the local cart).
    setItems([]);
  };

  const isInCart = (id: string) => items.some((item) => item.id === id);

  const total = items.reduce((sum, item) => sum + item.price_cents, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, clearCart, isInCart, total, minutesUntilExpiry }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
